/**
 * ForesightHero — the Foresight scene's hero card.
 *
 * Renders the AI-composed "anticipated answer" surface: the most-asked
 * question at this place right now, the operator-grounded answer, the
 * citation, and a quiet evidence footnote — NOT a social-proof badge.
 *
 * Research corrections applied (2026-05-15):
 *   1. Aggregate count is footnote, not headline (UX Collective / NN/g).
 *   2. q-line is 16px minimum (CLAUDE.md + NN/g glanceable-typography).
 *   3. "Not what you meant?" single-tap exit ramp (Slack/Linear pattern).
 *   4. Single-hero rule — memory band must not co-render (Apple Wallet).
 *
 * Spec: docs/strategy/visitor-pwa-direction-5-foreseeing-place-2026-05-15.html
 */
import { useTranslation } from 'react-i18next';
import type { Anticipated, SourceLabel } from '@tendedloop/shared';

const SOURCE_LABELS: Record<SourceLabel, string> = {
  'building-knowledge': 'building knowledge',
  status: 'live status',
  schedule: 'schedule',
  aggregate: 'aggregate signals',
  'operator-note': 'operator note',
};

export interface ForesightHeroProps {
  anticipated: Anticipated;
  onDismiss: () => void;
  /** Optional: visitor taps "ask follow-up" → opens chat with the question pre-seeded. */
  onFollowup?: (question: string) => void;
}

export function ForesightHero({ anticipated, onDismiss, onFollowup }: ForesightHeroProps) {
  const { t } = useTranslation('visitor');
  const sourceLabel = SOURCE_LABELS[anticipated.sourceLabel] ?? anticipated.sourceLabel;
  return (
    <section className="mt-6 mb-2 animate-fade-in" aria-live="polite" data-testid="foresight-hero">
      <article
        className="p-4"
        style={{
          background: 'var(--dir-bg-paper)',
          border: 'var(--dir-card-border)',
          borderRadius: 'var(--dir-card-radius)',
          boxShadow: 'var(--dir-card-shadow, 0 10px 24px -16px rgba(10,14,20,0.15))',
        }}
      >
        {/* Question — 16px floor, bold, italic for natural-prose feel.
            In Atelier the display-italic var pulls the serif slant in;
            Concierge leaves it upright. */}
        <h2
          className="text-[16px] font-bold leading-tight tracking-[-0.01em]"
          style={{
            color: 'var(--dir-ink-primary)',
            fontFamily: 'var(--dir-font-display)',
            fontStyle: 'var(--dir-display-italic)' as React.CSSProperties['fontStyle'],
          }}
          dir="auto"
        >
          {anticipated.question}
        </h2>
        {/* Answer */}
        <p
          className="mt-2 text-[15px] font-medium leading-relaxed"
          style={{ color: 'var(--dir-ink-primary)', fontFamily: 'var(--dir-font-body)' }}
          dir="auto"
        >
          {anticipated.answer}
        </p>
        {/* Citation — the operator-accountability stamp */}
        <p
          className="mt-2 text-[11px] italic"
          style={{ color: 'var(--dir-ink-muted)', fontFamily: 'var(--dir-font-display)' }}
          dir="auto"
        >
          —&nbsp;{sourceLabel}
        </p>
        {/* Quiet evidence footnote — NOT a social-proof badge */}
        <p
          className="text-[11px] italic"
          style={{ color: 'var(--dir-ink-muted)', fontFamily: 'var(--dir-font-display)' }}
          dir="auto"
        >
          {t('v3.foresight.footnote', 'based on recent questions at this place')}
        </p>
        {/* Optional follow-up CTA — primary if present.
            Both directions get a solid charcoal fill — distinguishes from
            secondary "ask else" in any direction. */}
        {onFollowup ? (
          <button
            type="button"
            onClick={() => onFollowup(anticipated.question)}
            className="mt-4 w-full rounded-full px-4 py-2.5 text-[13px] font-bold"
            style={{
              background: 'var(--dir-ink-primary)',
              color: 'var(--dir-bg-paper)',
              transition: 'opacity var(--dir-motion-fast-dur) var(--dir-motion-fast-curve)',
            }}
          >
            {t('v3.foresight.followup', 'Ask a follow-up')}
          </button>
        ) : null}
        {/* Dismiss exit ramp — single tap, no confirm.
            Atelier paints this gold; Concierge paints it green. */}
        <button
          type="button"
          onClick={onDismiss}
          className="mt-3 block w-full text-start text-[11.5px] font-semibold"
          style={{
            color: 'var(--dir-accent-primary)',
            textDecoration: 'underline dashed',
            textUnderlineOffset: '3px',
          }}
          data-testid="foresight-dismiss"
        >
          {t('v3.foresight.dismiss', 'Not what you meant? →')}
        </button>
      </article>
    </section>
  );
}
